import React, { useState } from "react";
import { useQuery } from "../../hooks/useQuery";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";

const PERM_GROUPS = [
  { title: "Shipment Modification", keys: ["can_modify_delivery_address", "can_modify_package_weight_or_dimensions", "can_create_shipments"] },
  { title: "Status Management", keys: ["can_receive_package", "can_update_transit_status", "can_mark_as_delivered", "can_override_status"] },
  { title: "Client Data", keys: ["can_view_client_info", "can_update_client_info"] },
  { title: "System", keys: ["can_void_shipment_or_refund", "can_reprint_labels", "can_manage_users", "can_manage_roles", "can_view_audit_log"] },
];

const labelize = (k) => k.replace(/^can_/, "").replace(/_/g, " ");

export default function Roles() {
  const { data, loading, reload } = useQuery(() => api.get("/roles").then((r) => r.data));
  const [drafts, setDrafts] = useState({});
  const [creating, setCreating] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [saving, setSaving] = useState({});

  const getPerms = (role) => drafts[role.name] || role.permissions;

  const togglePerm = (role, key) => {
    const current = getPerms(role);
    setDrafts((d) => ({ ...d, [role.name]: { ...current, [key]: !current[key] } }));
  };

  const saveRole = async (role) => {
    setSaving((s) => ({ ...s, [role.name]: true }));
    try {
      await api.patch(`/roles/${role.name}`, { permissions: getPerms(role) });
      toast.success(`Role ${role.label} saved`);
      setDrafts((d) => { const c = { ...d }; delete c[role.name]; return c; });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving((s) => ({ ...s, [role.name]: false }));
    }
  };

  const createRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      await api.post("/roles", { name: newRoleName, label: newRoleName });
      toast.success("Role created");
      setNewRoleName("");
      setCreating(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteRole = async (name) => {
    if (!window.confirm(`Delete role "${name}"?`)) return;
    try {
      await api.delete(`/roles/${name}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="roles-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="font-serif-bv text-3xl text-[#1B3022]">Role Management</h1>
          <p className="text-sm text-[#1B3022]/60">Granular permission matrix — toggle and save.</p>
        </div>
        {!creating ? (
          <button onClick={() => setCreating(true)} data-testid="new-role-btn" className="btn-gold rounded-sm px-4 py-2 text-xs inline-flex items-center gap-2">
            <Plus size={12} /> New role
          </button>
        ) : (
          <div className="flex gap-2">
            <input data-testid="new-role-name" autoFocus value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="e.g. dispatcher" className="px-3 py-2 border border-[#1B3022]/30 bg-white rounded-sm text-sm" />
            <button onClick={createRole} className="btn-gold rounded-sm px-4 py-2 text-xs">Create</button>
            <button onClick={() => setCreating(false)} className="px-3 py-2 text-xs text-[#1B3022]/60">Cancel</button>
          </div>
        )}
      </div>

      {loading && <p className="mt-8"><Loader2 className="inline animate-spin" /> Loading…</p>}

      <div className="mt-8 space-y-6">
        {(data?.roles || []).map((role) => {
          const perms = getPerms(role);
          const isAdmin = role.name === "admin";
          const dirty = !!drafts[role.name];
          return (
            <div key={role.name} data-testid={`role-card-${role.name}`} className="bg-white border border-[#1B3022]/15 rounded-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-[#1B3022]/10 flex items-center justify-between bg-[#F5EFE0]">
                <div>
                  <div className="font-serif-bv text-xl text-[#1B3022]">{role.label}</div>
                  <div className="text-[10px] tracking-widest uppercase text-[#1B3022]/60 font-mono">{role.name} {role.system && "• system"}</div>
                </div>
                <div className="flex gap-2">
                  {!isAdmin && (
                    <>
                      <button onClick={() => saveRole(role)} disabled={!dirty || saving[role.name]} data-testid={`save-role-${role.name}`} className="btn-gold rounded-sm px-4 py-1.5 text-xs disabled:opacity-40 inline-flex items-center gap-1">
                        {saving[role.name] ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save
                      </button>
                      {!role.system && (
                        <button onClick={() => deleteRole(role.name)} className="p-1.5 border border-[#C8102E]/50 rounded-sm hover:bg-[#C8102E]/10">
                          <Trash2 size={12} className="text-[#C8102E]" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="p-5 grid md:grid-cols-2 gap-6">
                {PERM_GROUPS.map((g) => (
                  <div key={g.title}>
                    <div className="text-[10px] tracking-widest uppercase text-[#A88420] mb-3">{g.title}</div>
                    <div className="space-y-2">
                      {g.keys.map((k) => (
                        <label key={k} data-testid={`perm-${role.name}-${k}`} className="flex items-center gap-3 text-sm">
                          <input
                            type="checkbox"
                            checked={!!perms[k]}
                            disabled={isAdmin}
                            onChange={() => togglePerm(role, k)}
                            className="h-4 w-4 accent-[#D4AF37]"
                          />
                          <span className="text-[#1B3022] capitalize">{labelize(k)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
